import { useHistory } from 'react-router';

import {
  ALGO_API_PAGE,
  ALGO_COMMUNITY_LINK,
  CCXT_CLIENT,
  DELTA_PYTHON_CLIENT,
  DOCS_URL,
} from 'constants/constants';
import routes, { LANDING_PAGE_ROUTES } from 'constants/routes';
import { SIGNAL_TRADING_TUTORIAL_BLOG } from 'constants/urls';
import { noop } from 'helpers/utils';
import i18n from 'i18n/config';
import { isCopilotEnabledSelector } from 'selectors/otherSelectors';
import { useAppSelector } from 'storeHooks';
import {
  AlgoApisIcon,
  AlgoCommunity,
  AngleLeft,
  ApiCopilot,
  BlogIcon,
  TradeHistoryIcon,
  TradingSignalBorderIcon,
} from 'styles/assets/icons';
import Box from 'UIKit/Box';
import IconButton from 'UIKit/IconButton';

import { FeatureCard } from './FeatureCard';
import { InformationCard } from './InformationCard';
import classes from './MobileAlgoResources.module.scss';
import type { FeatureCardProps, InformationCardProps } from './types';

const algoText = i18n.t('account:algoResources.algo');

const informationCards: InformationCardProps[] = [
  {
    name: i18n.t('account:algoResources.informationCardTitles.apiDocs'),
    icon: <BlogIcon className={classes.blogIcon} height={20} width={20} />,
    link: DOCS_URL,
  },
  {
    name: i18n.t('account:algoResources.informationCardTitles.ccxtIntegration'),
    icon: (
      <img
        src="https://static.delta.exchange/landing-page/api-guide/ccxt-integration.svg"
        height="100%"
        width="100%"
        alt="CCXT Integration"
      />
    ),
    link: CCXT_CLIENT,
  },
  {
    name: i18n.t('account:algoResources.informationCardTitles.deltaRestClient'),
    icon: (
      <img
        src="https://static.delta.exchange/landing-page/api-guide/python.svg"
        height="100%"
        width="100%"
        alt="Delta Rest Client"
      />
    ),
    link: DELTA_PYTHON_CLIENT,
  },
  {
    name: i18n.t('account:algoResources.informationCardTitles.algoCommunity'),
    icon: <AlgoCommunity />,
    link: ALGO_COMMUNITY_LINK,
  },
];

const informationText = i18n.t('account:algoResources.informationTitle');

const MobileAlgoResources = () => {
  const isCopilotEnabled = useAppSelector(isCopilotEnabledSelector);
  const history = useHistory();

  const featureCards: FeatureCardProps[] = [
    {
      label: i18n.t('account:algoResources.featureCardTitles.apis'),
      description: i18n.t('account:algoResources.featureCardDescriptions.apis'),
      icon: <AlgoApisIcon className={classes.algoApiIcon} width={20} height={20} />,
      link: ALGO_API_PAGE,
      target: '_self',
      show: true,
    },
    {
      label: i18n.t('account:algoResources.featureCardTitles.tradingBot'),
      description: i18n.t('account:algoResources.featureCardDescriptions.tradingBot'),
      icon: (
        <TradingSignalBorderIcon
          className={classes.signalTradingIcon}
          width={20}
          height={20}
          viewBox="0 0 22 20"
        />
      ),
      link: SIGNAL_TRADING_TUTORIAL_BLOG,
      target: '_blank',
      show: true,
    },
    {
      label: i18n.t('account:algoResources.featureCardTitles.tradeData'),
      description: i18n.t('account:algoResources.featureCardDescriptions.tradeData'),
      icon: (
        <TradeHistoryIcon
          fill1="var(--main-text-secondary)"
          fill3="var(--main-text-secondary)"
          width={20}
          height={20}
        />
      ),
      link: `app${routes.tradeHistory}`,
      target: '_self',
      show: true,
    },
    {
      label: i18n.t('account:algoResources.featureCardTitles.apiCopilot'),
      description: i18n.t('account:algoResources.featureCardDescriptions.apiCopilot'),
      icon: <ApiCopilot height={20} width={20} viewBox="0 0 23 24" />,
      link: LANDING_PAGE_ROUTES.copilot,
      target: '_self',
      show: isCopilotEnabled,
    },
  ];
  return (
    <>
      <Box className={classes.header}>
        <IconButton
          icon={<AngleLeft />}
          onClick={() => history.replace(routes.homepage.default)}
          data-testid="algo-resource-back-button"
          onKeyDown={noop}
        />
        <Box as="span" className={classes.headerText}>
          {algoText}
        </Box>
      </Box>
      <Box className={classes.root}>
        <Box className={classes.featureWrapper}>
          {featureCards
            .filter(featureCard => featureCard.show)
            .map(featureCard => {
              return (
                <FeatureCard
                  key={featureCard.label}
                  label={featureCard.label}
                  description={featureCard.description}
                  icon={featureCard.icon}
                  link={featureCard.link}
                  target={featureCard.target}
                />
              );
            })}
        </Box>
        <Box className={classes.informationWrapper}>
          <Box as="span" className={classes.infoTitle}>
            {informationText}
          </Box>
          <Box className={classes.featureWrapper}>
            {informationCards.map(informationCard => {
              return (
                <InformationCard
                  key={informationCard.name}
                  name={informationCard.name}
                  icon={informationCard.icon}
                  link={informationCard.link}
                />
              );
            })}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export { MobileAlgoResources };
